import { formatKg, kgToG, type RecipeRowFull, type StockRow } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { request } from '../../sync/api.js';

/**
 * Рецептуры замеса и дни, с которых они действуют.
 *
 * Прежняя рецептура не правится: новая закрывает её тем же днём. Отчёт за
 * прошлую неделю должен и через год считаться по тому, что действовало тогда,
 * иначе задним числом «поедут» все расхождения по расходу.
 */

interface RecipesResponse {
  recipes: RecipeRowFull[];
}

const SUBSTITUTE_GROUP = 'POLYMER';

interface StockResponse {
  stock: StockRow[];
}

function tomorrow(): string {
  return new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function RecipesScreen(): JSX.Element {
  const [recipes, setRecipes] = useState<RecipeRowFull[] | null>(null);
  const [materials, setMaterials] = useState<StockRow[]>([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [batchKg, setBatchKg] = useState('100');
  const [validFrom, setValidFrom] = useState(tomorrow());
  const [rows, setRows] = useState<Record<string, { min: string; max: string }>>({});
  /**
   * Взаимозаменяемое сырьё. Помеченные строки проверяются по сумме: капсулят
   * идёт в замещение или в дополнение агломерата, и порознь такое правило
   * не выразить. Вилка у них поэтому обязана быть одна на всех.
   */
  const [groups, setGroups] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [list, stock] = await Promise.all([
        request<RecipesResponse>('/api/recipes', { method: 'GET' }, 20_000),
        request<StockResponse>('/api/stock', { method: 'GET' }, 20_000),
      ]);
      setRecipes(list.recipes);
      setMaterials(stock.stock);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить рецептуры');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (): Promise<void> => {
    const items = Object.entries(rows)
      .filter(([, value]) => value.min.trim() !== '' || value.max.trim() !== '')
      .map(([materialTypeId, value]) => ({
        materialTypeId,
        min: Number((value.min || value.max).replace(',', '.')),
        max: Number((value.max || value.min).replace(',', '.')),
        group: groups.includes(materialTypeId) ? SUBSTITUTE_GROUP : null,
      }));

    if (items.length === 0) {
      setError('Добавьте хотя бы одно сырьё');
      return;
    }
    if (items.some((item) => !Number.isFinite(item.min) || !Number.isFinite(item.max))) {
      setError('Расход — число килограммов на замес');
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await request<unknown>(
        '/api/recipes',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            batchWeightG: kgToG(Number(batchKg.replace(',', '.'))),
            validFrom,
            items: items.map((item) => ({
              materialTypeId: item.materialTypeId,
              qtyPerBatchMinG: kgToG(item.min),
              qtyPerBatchMaxG: kgToG(item.max),
              substituteGroup: item.group,
            })),
          }),
        },
        30_000,
      );

      await load();
      setAdding(false);
      setName('');
      setRows({});
      setNotice('Рецептура добавлена. Прежняя закрыта этим же днём');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось сохранить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Рецептуры</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {error && <p className="mb-4 rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base">{error}</p>}
      {notice && (
        <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-base text-body">{notice}</p>
      )}

      {!adding && (
        <button
          type="button"
          onClick={() => {
            setAdding(true);
            setValidFrom(tomorrow());
            setNotice(null);
          }}
          className="mb-6 w-full rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Добавить рецептуру
        </button>
      )}

      {adding && (
        <section className="mb-6 rounded-xl bg-card shadow-card p-4">
          <h2 className="mb-1 text-lg font-semibold">Новая рецептура</h2>
          <p className="mb-4 text-base text-dim">
            Она начнёт действовать с выбранного дня, а прежняя закроется этим же днём.
            Задним числом рецептуру завести нельзя: уже сданные смены пересчитались бы.
          </p>

          <div className="flex flex-col gap-3">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Название, например «Летняя, тёмная»"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />

            <label className="flex flex-col gap-1 text-base text-dim">
              Вес замеса, кг
              <input
                value={batchKg}
                onChange={(event) => setBatchKg(event.target.value)}
                inputMode="decimal"
                className="rounded-xl bg-muted px-4 py-3 text-base tabular-nums text-strong"
              />
            </label>

            <label className="flex flex-col gap-1 text-base text-dim">
              Действует с
              <input
                value={validFrom}
                onChange={(event) => setValidFrom(event.target.value)}
                type="date"
                className="rounded-xl bg-muted px-4 py-3 text-base text-strong"
              />
            </label>

            <p className="mt-2 text-base text-dim">
              Расход на замес, кг — от и до. Технолог держит допуск, а не одно число.
            </p>
            <p className="text-base text-faint">
              Галочка «взаимозаменяемо» означает, что сырьё проверяется по сумме с другим
              отмеченным: капсулят идёт в замещение или в дополнение агломерата. У таких
              строк вилка должна совпадать.
            </p>
            {materials.map((material) => (
              <div key={material.materialTypeId} className="flex flex-wrap items-center gap-3">
                <span className="flex-1 text-base">{material.name}</span>
                <label className="flex items-center gap-2 text-base text-dim">
                  <input
                    type="checkbox"
                    checked={groups.includes(material.materialTypeId)}
                    onChange={() =>
                      setGroups(
                        groups.includes(material.materialTypeId)
                          ? groups.filter((id) => id !== material.materialTypeId)
                          : [...groups, material.materialTypeId],
                      )
                    }
                    className="h-5 w-5"
                  />
                  взаимозаменяемо
                </label>
                <input
                  value={rows[material.materialTypeId]?.min ?? ''}
                  onChange={(event) =>
                    setRows({
                      ...rows,
                      [material.materialTypeId]: {
                        min: event.target.value,
                        max: rows[material.materialTypeId]?.max ?? '',
                      },
                    })
                  }
                  inputMode="decimal"
                  placeholder="от"
                  className="w-24 rounded-xl bg-muted px-3 py-3 text-base tabular-nums"
                />
                <input
                  value={rows[material.materialTypeId]?.max ?? ''}
                  onChange={(event) =>
                    setRows({
                      ...rows,
                      [material.materialTypeId]: {
                        min: rows[material.materialTypeId]?.min ?? '',
                        max: event.target.value,
                      },
                    })
                  }
                  inputMode="decimal"
                  placeholder="до"
                  className="w-24 rounded-xl bg-muted px-3 py-3 text-base tabular-nums"
                />
              </div>
            ))}
          </div>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              disabled={busy || name.trim().length < 2}
              onClick={() => void submit()}
              className="flex-1 rounded-xl bg-sky-700 px-5 py-4 text-lg font-semibold disabled:opacity-50"
            >
              {busy ? 'Сохраняю…' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded-xl bg-muted px-5 py-4 text-lg font-semibold"
            >
              Отмена
            </button>
          </div>
        </section>
      )}

      {recipes === null && !error && <p className="text-base text-dim">Загружаю…</p>}

      <div className="flex flex-col gap-3">
        {(recipes ?? []).map((recipe) => (
          <article
            key={recipe.id}
            className={`rounded-xl p-4 ${recipe.validTo === null ? 'bg-card shadow-card' : 'bg-canvas'}`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-lg font-semibold">{recipe.name}</p>
              <p className="text-base text-dim">
                {recipe.validTo === null ? 'действует' : 'закрыта'}
              </p>
            </div>
            <p className="text-base text-dim">
              с {recipe.validFrom}
              {recipe.validTo ? ` по ${recipe.validTo}` : ''} · замес{' '}
              {formatKg(recipe.batchWeightG)} кг
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {recipe.items.map((item) => (
                <li key={item.materialTypeId} className="text-base text-body">
                  {item.name}: {formatKg(item.qtyPerBatchMinG)}–{formatKg(item.qtyPerBatchMaxG)} кг
                  {item.substituteGroup ? ' · взаимозаменяемо' : ''}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  );
}
